/**
 * MFX OS — business command layer (client-side, Release 1 form).
 *
 * The UI never flips controlled status fields directly: every controlled
 * action goes through a command here, which validates hard rules, writes
 * atomically (batch/transaction), appends an osEvents entry, and enqueues
 * outbox intents. This module is the single choke point that later lifts
 * into Cloud Functions (same names as the blueprint command list).
 */
import {
  collection, doc, getDocs, limit, query, runTransaction, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  DEFAULT_TRACKS, GATE_DEFS,
  type CustomerPO, type Gate, type GateCode, type GateStatus, type Item,
  type JobTicketOS, type POException, type POLine, type ProductionJob,
  type SalesOrderLine, type SalesOrderOS, type BlueprintRevision, type JobReadiness,
} from './types';
import { actor, appendEvent, enqueueOutbox, newMeta, nextDisplayId, nowIso, touch, getOsDoc } from './core';
import { autoMapLines, compare, quoteClient, quoteEmail, type QuoteDoc } from './compare';

type PO = CustomerPO & { id: string };
type SO = SalesOrderOS & { id: string };
type Line = SalesOrderLine & { id: string };
type Job = ProductionJob & { id: string };

const ref = (coll: string, id: string) => doc(db, coll, id);
const fail = (msg: string): never => { throw new Error(msg); };

// ═══ Stage 1 — receiveCustomerPO ═════════════════════════════════════════
export interface ReceivePOInput {
  poNumber: string;
  clientName: string;
  clientEmail?: string;
  channel: CustomerPO['channel'];
  total?: number;
  requestedDate?: string;
  shipTo?: string;
  billTo?: string;
  paymentTerms?: string;
  notes?: string;
  fileUrl?: string;
  rawText?: string;
  lines: POLine[];
  portalQuoteDocId?: string;
  quote?: QuoteDoc | null;
}

export async function receiveCustomerPO(input: ReceivePOInput): Promise<string> {
  if (!input.poNumber.trim()) fail('PO number is required.');
  if (!input.clientName.trim()) fail('Client is required.');

  const dupSnap = await getDocs(query(collection(db, 'customerPOs'), where('poNumber', '==', input.poNumber.trim()), limit(5)));
  const duplicate = dupSnap.docs.find((d) => String(d.data().clientName || '').toLowerCase() === input.clientName.trim().toLowerCase());

  const displayId = await nextDisplayId('PO');
  const id = displayId.toLowerCase();
  const po: CustomerPO = {
    ...newMeta(displayId, 'RECEIVED', { clientName: input.clientName.trim() }),
    status: 'RECEIVED',
    poNumber: input.poNumber.trim(),
    clientEmail: (input.clientEmail || '').toLowerCase() || undefined,
    channel: input.channel,
    receivedAt: nowIso(),
    reviewer: actor().email,
    total: input.total,
    requestedDate: input.requestedDate,
    shipTo: input.shipTo,
    billTo: input.billTo,
    paymentTerms: input.paymentTerms,
    notes: input.notes,
    fileUrl: input.fileUrl,
    rawText: input.rawText,
    lines: input.lines,
    portalQuoteDocId: input.portalQuoteDocId,
    ...(duplicate ? { duplicateOfId: duplicate.id } : {}),
  } as CustomerPO;

  const batch = writeBatch(db);
  batch.set(ref('customerPOs', id), po as unknown as Record<string, unknown>);
  await batch.commit();
  await appendEvent({ entityType: 'customerPO', entityId: id, displayId, action: 'PO received', to: 'RECEIVED', reason: `${input.channel} · ${input.clientName}` });
  await enqueueOutbox('CUSTOMER_PO_RECEIVED', { customerPOId: id, displayId, poNumber: po.poNumber, clientName: po.clientName });

  if (input.quote) await matchPOToQuote(id, input.quote);
  return id;
}

// ═══ Stage 2 — matchPOToQuote ════════════════════════════════════════════
export async function matchPOToQuote(poId: string, quote: QuoteDoc): Promise<void> {
  const po = await getOsDoc<CustomerPO>('customerPOs', poId) ?? fail('PO not found.');
  const cmp = compare({ ...po, clientName: po.clientName || quoteClient(quote) }, quote, { duplicate: !!po.duplicateOfId });
  const lines = autoMapLines(po.lines, quote);
  await runTransaction(db, async (tx) => {
    tx.update(ref('customerPOs', poId), touch({
      quoteId: quote.id,
      quoteNum: cmp.quoteNum,
      clientName: po.clientName || quoteClient(quote),
      clientEmail: po.clientEmail || quoteEmail(quote) || undefined,
      lines,
      comparison: cmp,
      status: 'REVIEW_REQUIRED',
      version: (po.version || 1) + 1,
    }) as never);
  });
  await appendEvent({
    entityType: 'customerPO', entityId: poId, displayId: po.displayId,
    action: 'Matched against quote', from: po.status, to: 'REVIEW_REQUIRED',
    reason: cmp.blockers.length ? `${cmp.blockers.length} blocker(s)` : 'clean match',
    related: [{ type: 'quote', id: quote.id, displayId: cmp.quoteNum }],
  });
}

// ═══ Stage 2 — review actions ════════════════════════════════════════════
export async function approveCustomerPO(po: PO, exception?: POException): Promise<void> {
  if (po.status === 'APPROVED') fail('PO is already approved.');
  const cmp = po.comparison;
  if (!cmp) throw new Error('Run the quote comparison first.');
  const blockers = cmp.blockers || [];
  if (blockers.length && !exception) fail(`Cannot approve — ${blockers.length} hard rule(s) unresolved. Approve with an authorized exception or resolve them.`);
  await runTransaction(db, async (tx) => {
    tx.update(ref('customerPOs', po.id), touch({
      status: 'APPROVED',
      ...(exception ? { exception: { ...exception, approvedBy: actor().email, approvedAt: nowIso() } } : {}),
      version: (po.version || 1) + 1,
    }) as never);
  });
  await appendEvent({
    entityType: 'customerPO', entityId: po.id, displayId: po.displayId,
    action: exception ? 'PO approved WITH EXCEPTION' : 'PO approved',
    from: po.status, to: 'APPROVED',
    reason: exception ? `${exception.category}: ${exception.reason}` : 'all hard rules satisfied',
    evidence: exception?.riskNote,
  });
}

export async function requestClientClarification(po: PO, question: string): Promise<void> {
  if (!question.trim()) fail('Clarification question is required.');
  await runTransaction(db, async (tx) => {
    tx.update(ref('customerPOs', po.id), touch({
      status: 'WAITING_ON_CLIENT',
      clarification: { question: question.trim(), askedAt: nowIso(), askedBy: actor().email },
      version: (po.version || 1) + 1,
    }) as never);
  });
  await appendEvent({ entityType: 'customerPO', entityId: po.id, displayId: po.displayId, action: 'Clarification requested', from: po.status, to: 'WAITING_ON_CLIENT', reason: question.trim() });
  await enqueueOutbox('PO_CLARIFICATION_REQUESTED', { customerPOId: po.id, question: question.trim(), clientEmail: po.clientEmail || '' });
}

export async function resolveClientClarification(po: PO, answer: string): Promise<void> {
  await runTransaction(db, async (tx) => {
    tx.update(ref('customerPOs', po.id), touch({
      status: 'REVIEW_REQUIRED',
      clarification: { ...(po.clarification || { question: '', askedAt: nowIso(), askedBy: actor().email }), answer: answer.trim(), resolvedAt: nowIso() },
      version: (po.version || 1) + 1,
    }) as never);
  });
  await appendEvent({ entityType: 'customerPO', entityId: po.id, displayId: po.displayId, action: 'Clarification resolved', from: 'WAITING_ON_CLIENT', to: 'REVIEW_REQUIRED', reason: answer.trim() });
}

export async function rejectCustomerPO(po: PO, reason: string): Promise<void> {
  if (!reason.trim()) fail('A rejection reason is required.');
  if (po.salesOrderId) fail('PO already has a sales order — supersede instead.');
  await runTransaction(db, async (tx) => {
    tx.update(ref('customerPOs', po.id), touch({ status: 'REJECTED', version: (po.version || 1) + 1 }) as never);
  });
  await appendEvent({ entityType: 'customerPO', entityId: po.id, displayId: po.displayId, action: 'PO rejected', from: po.status, to: 'REJECTED', reason: reason.trim() });
}

// ═══ Stage 3 — createSalesOrderFromPO (one controlled command) ═══════════
function buildAckBody(so: SalesOrderOS, lines: SalesOrderLine[]): string {
  const L = lines.map((l) => `  • ${l.description || l.customerPart} — ${l.qty.toLocaleString()} @ $${l.unitPrice.toFixed(4)} = $${l.total.toFixed(2)}`).join('\n');
  return [
    `ORDER ACKNOWLEDGMENT — Microflex Film`,
    ``,
    `Sales Order: ${so.soNum}`,
    `Customer PO: ${so.poNumber}`,
    `Customer: ${so.customerName}`,
    ``,
    `Lines:`,
    L,
    ``,
    `Order total: $${so.total.toFixed(2)}`,
    so.requestedDate ? `Requested date: ${so.requestedDate}` : '',
    so.promisedDate ? `Committed date: ${so.promisedDate}` : `Committed date: pending scheduling confirmation`,
    so.email ? `Portal: your order is visible in your Microflex portal account (${so.email}).` : '',
    ``,
    `Thank you — we'll confirm production scheduling shortly.`,
  ].filter(Boolean).join('\n');
}

export async function createSalesOrderFromPO(po: PO): Promise<string> {
  if (po.status !== 'APPROVED') fail('PO must be APPROVED before creating a sales order.');
  if (po.salesOrderId) fail(`Sales order ${po.salesOrderNum || ''} already exists for this PO.`);

  const displayId = await nextDisplayId('SO');
  const soId = displayId.toLowerCase();
  const priceException = po.exception?.category === 'pricing';

  const lines: SalesOrderLine[] = po.lines.map((l, i) => ({
    ...newMeta(`${displayId}-L${i + 1}`, 'OPEN', { clientName: po.clientName }),
    salesOrderId: soId,
    soNum: displayId,
    lineNo: i + 1,
    customerPart: l.customerPart,
    description: l.description,
    qty: l.qty,
    // preserve quote pricing unless a pricing exception was formally approved
    unitPrice: priceException ? l.unitPrice : (l.quoteUnitPrice ?? l.unitPrice),
    poUnitPrice: l.unitPrice,
    total: (priceException ? l.unitPrice : (l.quoteUnitPrice ?? l.unitPrice)) * l.qty,
    requestedDate: po.requestedDate,
    resolution: 'unresolved',
  }));

  const total = lines.reduce((s, l) => s + l.total, 0);
  const so: SalesOrderOS = {
    ...newMeta(displayId, 'confirmed', { clientName: po.clientName }),
    soNum: displayId,
    customerName: po.clientName || '',
    email: po.clientEmail,
    poNumber: po.poNumber,
    total,
    skus: lines.map((l) => l.customerPart || l.description).filter(Boolean),
    skuCount: lines.length,
    requestedDate: po.requestedDate,
    poRequiredDate: po.requestedDate,
    customerPOId: po.id,
    quoteId: po.quoteId,
    quoteNum: po.quoteNum,
    quoteLock: po.quoteId ? { quoteId: po.quoteId, quoteNum: po.quoteNum || '', lockedAt: nowIso() } : undefined,
    poLock: { customerPOId: po.id, poNumber: po.poNumber, lockedAt: nowIso(), poVersion: po.version || 1 },
    tracks: { ...DEFAULT_TRACKS },
    lineIds: lines.map((l) => l.displayId.toLowerCase()),
    jobTicketIds: [],
  } as SalesOrderOS;
  so.ack = { preparedAt: nowIso(), body: buildAckBody(so, lines) };

  const batch = writeBatch(db);
  const clean = JSON.parse(JSON.stringify(so)) as Record<string, unknown>; // drop undefined
  batch.set(ref('salesOrders', soId), clean);
  for (const l of lines) batch.set(ref('salesOrderLines', l.displayId.toLowerCase()), JSON.parse(JSON.stringify(l)));
  batch.update(ref('customerPOs', po.id), touch({ salesOrderId: soId, salesOrderNum: displayId, version: (po.version || 1) + 1 }) as never);
  await batch.commit();

  await appendEvent({ entityType: 'customerPO', entityId: po.id, displayId: po.displayId, action: 'Sales order created from PO', related: [{ type: 'salesOrder', id: soId, displayId }] });
  await appendEvent({ entityType: 'salesOrder', entityId: soId, displayId, action: 'Sales order created', to: 'APPROVED', reason: `from ${po.displayId} · quote ${po.quoteNum || '—'} locked`, related: [{ type: 'customerPO', id: po.id, displayId: po.displayId }] });
  await enqueueOutbox('SALES_ORDER_CREATED', { salesOrderId: soId, soNum: displayId, customerPOId: po.id, clientEmail: po.clientEmail || '' });
  return soId;
}

export async function markAckSent(so: SO): Promise<void> {
  await runTransaction(db, async (tx) => {
    tx.update(ref('salesOrders', so.id), touch({ 'ack.sentAt': nowIso(), 'ack.sentBy': actor().email, version: (so.version || 1) + 1 }) as never);
  });
  await appendEvent({ entityType: 'salesOrder', entityId: so.id, displayId: so.displayId, action: 'Acknowledgment sent to customer' });
}

// ═══ Stage 5 — resolveItemForOrderLine ═══════════════════════════════════
export interface ResolveExisting { mode: 'existing'; itemId: string }
export interface ResolveNew { mode: 'new'; name: string; customerPart?: string; family?: string; revReason?: string }
export interface ResolveChanged { mode: 'changed'; itemId: string; changeNote: string; revReason: string }
export type ResolveInput = ResolveExisting | ResolveNew | ResolveChanged;

export async function resolveItemForOrderLine(line: Line, input: ResolveInput): Promise<void> {
  const batch = writeBatch(db);
  let itemId = '';
  let internalPart = '';
  let revisionId: string | undefined;
  let revCode: string | undefined;
  let resolution: SalesOrderLine['resolution'];

  if (input.mode === 'existing') {
    const item = await getOsDoc<Item>('items', input.itemId) ?? fail('Item not found.');
    itemId = item.id; internalPart = item.internalPart;
    revisionId = item.currentRevisionId; revCode = item.currentRevCode;
    resolution = 'existing';
  } else if (input.mode === 'new') {
    const displayId = await nextDisplayId('ITM');
    itemId = displayId.toLowerCase();
    internalPart = displayId;
    const revId = `${itemId}-rev-a`;
    const item: Item = {
      ...newMeta(displayId, 'ACTIVE', { clientName: line.clientName }),
      internalPart: displayId,
      customerPart: input.customerPart || line.customerPart || undefined,
      name: input.name || line.description,
      family: input.family,
      currentRevisionId: revId,
      currentRevCode: 'A',
    } as Item;
    const rev: BlueprintRevision = {
      ...newMeta(`${displayId}-Rev A`, 'DRAFT', { clientName: line.clientName }),
      status: 'DRAFT',
      itemId, internalPart: displayId, revCode: 'A',
      reason: input.revReason || 'Initial onboarding (Rev A)',
      spec: {},
    } as BlueprintRevision;
    batch.set(ref('items', itemId), JSON.parse(JSON.stringify(item)));
    batch.set(ref('blueprintRevisions', revId), JSON.parse(JSON.stringify(rev)));
    revisionId = revId; revCode = 'A';
    resolution = 'new';
  } else {
    const item = await getOsDoc<Item>('items', input.itemId) ?? fail('Item not found.');
    itemId = item.id; internalPart = item.internalPart;
    const nextCode = String.fromCharCode(((item.currentRevCode || 'A').charCodeAt(0) - 64) % 26 + 65); // A→B→C
    const revId = `${item.id}-rev-${nextCode.toLowerCase()}`;
    const rev: BlueprintRevision = {
      ...newMeta(`${item.internalPart}-Rev ${nextCode}`, 'DRAFT', { clientName: line.clientName }),
      status: 'DRAFT',
      itemId: item.id, internalPart: item.internalPart, revCode: nextCode,
      reason: input.revReason,
      spec: {},
      supersedesId: item.currentRevisionId,
    } as BlueprintRevision;
    batch.set(ref('blueprintRevisions', revId), JSON.parse(JSON.stringify(rev)));
    revisionId = revId; revCode = nextCode;
    resolution = 'changed';
  }

  batch.update(ref('salesOrderLines', line.id), touch({
    resolution, itemId, internalPart,
    blueprintRevisionId: revisionId || null, blueprintRevCode: revCode || null,
    ...(input.mode === 'changed' ? { changeNote: input.changeNote } : {}),
    version: (line.version || 1) + 1,
  }) as never);
  await batch.commit();
  await appendEvent({
    entityType: 'salesOrderLine', entityId: line.id, displayId: line.displayId,
    action: `Line resolved → ${resolution} item`, to: resolution,
    reason: `${internalPart}${revCode ? ` · Rev ${revCode}` : ''}`,
    related: [{ type: 'item', id: itemId, displayId: internalPart }],
  });
}

export async function approveBlueprintRevision(rev: BlueprintRevision & { id: string }): Promise<void> {
  if (rev.status === 'APPROVED') fail('Revision already approved.');
  const batch = writeBatch(db);
  batch.update(ref('blueprintRevisions', rev.id), touch({ status: 'APPROVED', approvedBy: actor().email, approvedAt: nowIso(), effectiveAt: nowIso(), version: (rev.version || 1) + 1 }) as never);
  if (rev.supersedesId) batch.update(ref('blueprintRevisions', rev.supersedesId), touch({ status: 'SUPERSEDED' }) as never);
  batch.update(ref('items', rev.itemId), touch({ currentRevisionId: rev.id, currentRevCode: rev.revCode }) as never);
  await batch.commit();
  await appendEvent({ entityType: 'blueprintRevision', entityId: rev.id, displayId: rev.displayId, action: 'Blueprint revision approved', from: 'DRAFT', to: 'APPROVED', reason: rev.reason });
}

// ═══ Stage 6-7 — createJobTicket + production jobs with gates ════════════
function seedGates(line: Line): Gate[] {
  return GATE_DEFS.map((g) => {
    const base: Gate = { code: g.code, label: g.label, applicability: 'required', status: 'not_started', ownerRole: g.ownerRole };
    if (g.code === 'commercial') return { ...base, status: 'ready', evidence: 'PO approved + SO created', completedBy: actor().email, completedAt: nowIso() };
    if (g.code === 'prepress' && line.resolution === 'existing' && line.blueprintRevCode) {
      return { ...base, status: 'ready', evidence: `Existing item · Blueprint Rev ${line.blueprintRevCode} approved`, completedBy: actor().email, completedAt: nowIso() };
    }
    if (g.code === 'production') return { ...base, status: 'waiting', blockerReason: 'Schedule not assigned' };
    return base;
  });
}

export function jobReadiness(gates: Gate[]): JobReadiness {
  const req = gates.filter((g) => g.applicability === 'required');
  if (req.some((g) => g.status === 'blocked')) return 'blocked';
  if (req.every((g) => g.status === 'ready' || g.status === 'overridden' || g.status === 'not_applicable')) return 'ready';
  return 'partial';
}

export async function createJobTicket(so: SO, lines: Line[]): Promise<string> {
  if (!lines.length) fail('Sales order has no lines.');
  const unresolved = lines.filter((l) => l.resolution === 'unresolved');
  if (unresolved.length) fail(`${unresolved.length} line(s) not resolved to an item yet.`);
  if (so.jobTicketIds?.length) fail('Ticket already exists for this sales order (split tickets come later).');

  const displayId = await nextDisplayId('JT');
  const jtId = displayId.toLowerCase();
  const seq = displayId.split('-').pop() || '0000';

  const jobs: ProductionJob[] = lines.map((l, i) => {
    const nn = String(i + 1).padStart(2, '0');
    const gates = seedGates(l);
    return {
      ...newMeta(`JOB-${seq}-${nn}`, 'OPEN', { clientName: so.customerName }),
      status: 'OPEN',
      jobTicketId: jtId,
      ticketDisplayId: displayId,
      salesOrderId: so.id,
      soNum: so.soNum,
      salesOrderLineId: l.id,
      customerName: so.customerName,
      itemId: l.itemId,
      internalPart: l.internalPart,
      customerPart: l.customerPart,
      description: l.description,
      qty: l.qty,
      blueprintRevisionId: l.blueprintRevisionId,
      blueprintRevCode: l.blueprintRevCode,
      gates,
      readiness: jobReadiness(gates),
      runIds: [],
    } as ProductionJob;
  });

  const readyCount = jobs.reduce((s, j) => s + j.gates.filter((g) => g.status === 'ready' || g.status === 'overridden').length, 0);
  const gateCount = jobs.reduce((s, j) => s + j.gates.length, 0);
  const ticket: JobTicketOS = {
    ...newMeta(displayId, 'open', { clientName: so.customerName }),
    status: 'open',
    salesOrderId: so.id,
    soNum: so.soNum,
    customerName: so.customerName,
    dueDate: so.requestedDate,
    productionJobIds: jobs.map((j) => `${jtId}-j${String(jobs.indexOf(j) + 1).padStart(2, '0')}`),
    readinessPct: gateCount ? Math.round((readyCount / gateCount) * 100) : 0,
    blockedCount: jobs.filter((j) => j.readiness === 'blocked').length,
  } as JobTicketOS;

  const batch = writeBatch(db);
  batch.set(ref('jobTickets', jtId), JSON.parse(JSON.stringify(ticket)));
  jobs.forEach((j, i) => {
    const jobId = `${jtId}-j${String(i + 1).padStart(2, '0')}`;
    batch.set(ref('productionJobs', jobId), JSON.parse(JSON.stringify(j)));
    batch.update(ref('salesOrderLines', lines[i].id), touch({ productionJobId: jobId }) as never);
  });
  batch.update(ref('salesOrders', so.id), touch({ jobTicketIds: [...(so.jobTicketIds || []), jtId], version: (so.version || 1) + 1 }) as never);
  await batch.commit();

  await appendEvent({ entityType: 'jobTicket', entityId: jtId, displayId, action: 'Ticket created', to: 'open', reason: `${jobs.length} production job(s) from ${so.soNum}`, related: [{ type: 'salesOrder', id: so.id, displayId: so.soNum }] });
  await appendEvent({ entityType: 'salesOrder', entityId: so.id, displayId: so.displayId, action: 'Execution structure created', related: [{ type: 'jobTicket', id: jtId, displayId }] });
  await enqueueOutbox('JOB_TICKET_CREATED', { jobTicketId: jtId, displayId, salesOrderId: so.id });
  return jtId;
}

// ═══ Stage 7 — readiness gates ═══════════════════════════════════════════
export interface GatePatch { status: GateStatus; evidence?: string; blockerReason?: string; override?: Gate['override'] }

export async function updateReadinessGate(job: Job, code: GateCode, patch: GatePatch): Promise<void> {
  if (patch.status === 'blocked' && !patch.blockerReason?.trim()) fail('A blocked gate needs a reason.');
  if (patch.status === 'overridden' && (!patch.override?.reason || !patch.override?.riskNote)) fail('Override needs a reason and a risk note.');
  const prev = job.gates.find((g) => g.code === code) ?? fail(`Gate ${code} not found.`);

  const gates = job.gates.map((g) => g.code !== code ? g : ({
    ...g,
    status: patch.status,
    evidence: patch.evidence ?? g.evidence,
    blockerReason: patch.status === 'blocked' ? patch.blockerReason : undefined,
    override: patch.status === 'overridden' ? { ...patch.override!, approvedBy: actor().email, approvedAt: nowIso() } : g.override,
    completedBy: (patch.status === 'ready' || patch.status === 'overridden') ? actor().email : undefined,
    completedAt: (patch.status === 'ready' || patch.status === 'overridden') ? nowIso() : undefined,
  }));
  const readiness = jobReadiness(gates);
  const status = readiness === 'ready' && job.status === 'OPEN' ? 'READY' : readiness !== 'ready' && job.status === 'READY' ? 'OPEN' : job.status;

  await runTransaction(db, async (tx) => {
    tx.update(ref('productionJobs', job.id), touch({ gates: JSON.parse(JSON.stringify(gates)), readiness, status, version: (job.version || 1) + 1 }) as never);
  });
  await appendEvent({
    entityType: 'productionJob', entityId: job.id, displayId: job.displayId,
    action: `Gate ${prev.label}: ${prev.status} → ${patch.status}`,
    from: prev.status, to: patch.status,
    reason: patch.blockerReason || patch.override?.reason || patch.evidence,
    evidence: patch.override?.riskNote,
  });
  await syncTicketReadiness(job.jobTicketId);
}

/** Recompute ticket rollup + SO production track from its jobs. */
export async function syncTicketReadiness(jobTicketId: string): Promise<void> {
  const snap = await getDocs(query(collection(db, 'productionJobs'), where('jobTicketId', '==', jobTicketId)));
  const jobs = snap.docs.map((d) => d.data() as ProductionJob);
  if (!jobs.length) return;
  const gateCount = jobs.reduce((s, j) => s + j.gates.filter((g) => g.applicability === 'required').length, 0);
  const readyCount = jobs.reduce((s, j) => s + j.gates.filter((g) => g.applicability === 'required' && (g.status === 'ready' || g.status === 'overridden')).length, 0);
  const blockedCount = jobs.filter((j) => j.readiness === 'blocked').length;
  const allReady = jobs.every((j) => j.readiness === 'ready');
  const ticket = await getOsDoc<JobTicketOS>('jobTickets', jobTicketId);
  if (!ticket) return;
  const batch = writeBatch(db);
  batch.update(ref('jobTickets', jobTicketId), touch({
    readinessPct: gateCount ? Math.round((readyCount / gateCount) * 100) : 0,
    blockedCount,
    status: allReady && ticket.status === 'open' ? 'released-ready' : ticket.status,
  }) as never);
  if (ticket.salesOrderId) {
    const so = await getOsDoc<SalesOrderOS>('salesOrders', ticket.salesOrderId);
    if (so?.tracks) batch.update(ref('salesOrders', ticket.salesOrderId), { 'tracks.production': allReady ? 'READY' : 'NOT_READY' } as never);
  }
  await batch.commit();
}
